import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Add } from "@mui/icons-material";
import {
  Box,
  Typography,
  CircularProgress,
  DialogActions,
  Button,
  TextField,
  DialogTitle,
  Dialog,
  DialogContent,
  TableCell,
  TableBody,
  TableRow,
  TableHead,
  TableContainer,
  Paper,
  Table,
} from "@mui/material";
import callPiggybackAPI from "../Helpers/piggyback.api.helper";
import { useStyles } from "./UI Helpers/DataStyles";
import { IconButton } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import LinkModal from "./LinkModal";
import AllTemplates from "./AllTemplates";
import { refreshDigitalTags } from "../apiactions/digitalActions";
import { processTableData } from "./UI Helpers/PrcoessTableData";
import TruInkEditForm from "./TruInkEditForm";
import AddTruInkEMMSModal from "./AddTruInkEMMSModal";

import {
  gatewaysSchema,
  digitalTagsSchema,
  dataRecordSchema,
} from "../Helpers/formSchemas";
import { buildDataInfoSchema } from "../Helpers/formSchemas";
import {
  setSiteSchema,
  setTruInkDataInfoId,
} from "../../../redux/slices/truInk";
import TrueInkCollectionProfileModal from "./TruInkCollectionPrintModel";
import { enqueueSnackbar } from "notistack";
import AddCollectionModal from "./AddCollectionModal";

function TrunInkDataTable({ refreshKey, onSaveSuccess, templateSearchValue, setTemplateSearchValues }) {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 3,
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [collectionEditClicked, setCollectionEditClicked] = useState(false);
  const classes = useStyles();
  const selectedSiteOption = useSelector(
    (state) => state.truInk.selectedSiteOption
  );
  const selectedSite = useSelector((state) => state.truInk.selectedSite);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDataInfoId, setSelectedDataInfoId] = useState(null);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [tagToLink, setTagToLink] = useState(null);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [dataRecordOptions, setDataRecordOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const dispatch = useDispatch();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editRowData, setEditRowData] = useState(null);
  const [currentSchema, setCurrentSchema] = useState({});
  const [isEmmsModalOpen, setIsEmmsModalOpen] = useState(false);
  const [emmsSavedData, setEmmsSavedData] = useState(null);
  const [isFieldEditOpen, setIsFieldEditOpen] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState(null);
  const [collectionApplyModalOpen, setCollectionApplyModalOpen] =
    useState(false);
  const [templates, setTemplates] = useState([]);
  const [dataRecords, setDataRecords] = useState([]);
  const [selectedCollectionData, setSelectedCollectionData] = useState(null);
  const [selectedCollectionForEdit, setSelectedCollectionForEdit] =
    useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getDigitalTagSchemaWithOptions = async () => {
    return JSON.parse(JSON.stringify(digitalTagsSchema));
  };

  const getGatewaySchemaWithOptions = async () => {
    const schema = JSON.parse(JSON.stringify(gatewaysSchema));
    try {
      const queues = await callPiggybackAPI("queue", {}, {}, "GET");
      if (Array.isArray(queues)) {
        schema.queue.options = queues.map((q) => ({
          value: q.id,
          label: q.name,
        }));
      } else {
        schema.queue.options = [];
      }
    } catch (error) {
      console.error("Failed to fetch queues:", error);
      schema.queue.options = [];
    }
    return schema;
  };
  const getSchemaForOption = (option, rowData) => {
    switch (option) {
      case "SiteData":
        const { id, dataInfoId, ...cleanedRowData } = rowData;
        return dataRecordSchema(cleanedRowData, "labels" in cleanedRowData);
      case "Gateways":
        return gatewaysSchema;
      case "Digital Tags":
        return digitalTagsSchema;

      default:
        return {};
    }
  };
  const handleCollectionEditSave = async (updatedCollection) => {
    if (!selectedSite) return;
    let body = {};
    let path = "collections";
    body = {
      id: updatedCollection.id,
      siteId: selectedSite.id,
      tenantId: selectedSite.tenantId,
      name: updatedCollection.name,
      dataIds: updatedCollection.items.map((item) => item.id),
    };
    const apiResponse = await callPiggybackAPI(path, {}, body, "POST");
    if (apiResponse) {
      toast.success("Collection Saved successfully", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };
  const handleEditClick = async (path, id, rowData) => {
    if (path === "truInkEmms") {
      setEmmsSavedData(rowData);
      setIsEmmsModalOpen(true);
      return;
    }
    if (path === "collections") {
      const apiResponse = await callPiggybackAPI(
        "data-record",
        { siteId: selectedSite.id, tenantId: selectedSite.tenantId },
        {},
        "GET"
      );

      if (Array.isArray(apiResponse)) {
        const normalizedData = apiResponse.map((item, index) => ({
          id: item.id || `row-${index}`,
          identifier: item.identifier,
          dataInfoId: item.dataInfoId,
          ...item.values,
        }));
        setDataRecords(normalizedData);
      } else {
        setDataRecords([]);
      }
      setSelectedCollectionData(rowData);
      setCollectionEditClicked(true);
      return;
    }
    let schema = getSchemaForOption(selectedSiteOption, rowData);
    if (selectedSiteOption === "Digital Tags") {
      schema = await getDigitalTagSchemaWithOptions();
    } else if (selectedSiteOption === "Gateways") {
      schema = await getGatewaySchemaWithOptions();
    }

    setCurrentSchema(schema);

    setEditRowData(
      selectedSiteOption === "Digital Tags"
        ? {
          ...rowData,
          rotation:
            rowData?.rotation ?? rowData?.customProperties?.rotation ?? "",
          canvasSizeId: rowData?.canvasSizeId
            ? String(rowData.canvasSizeId)
            : rowData?.canvasSize?.id
              ? String(rowData.canvasSize.id)
              : "",
          canvasColorId: rowData?.canvasColorId
            ? String(rowData.canvasColorId)
            : rowData?.canvasColor?.id
              ? String(rowData.canvasColor.id)
              : "",
        }
        : selectedSiteOption === "Gateways"
          ? {
            ...rowData,
            queue: typeof rowData?.queue === "object"
              ? (rowData.queue?.id ?? "")
              : (rowData?.queue ?? ""),
          }
          : rowData
    );

    setIsEditModalOpen(true);
  };

  const handleUpdateSubmit = async (updatedData) => {
    if (!selectedSite) return;
    let body = {};
    let path = "";
    switch (selectedSiteOption) {
      case "SiteData":
        path = "data-record";
        const values = Object.keys(updatedData).reduce((acc, key) => {
          if (["id", "dataInfoId", "identifier", "recordedAt"].includes(key))
            return acc;

          acc[key] = Array.isArray(updatedData[key]) ? updatedData[key].join(", ") : updatedData[key];

          return acc;
        }, {});

        body = {
          id: updatedData.id,
          tenantId: selectedSite.tenantId,
          siteId: selectedSite.id,
          dataInfoId: updatedData.dataInfoId,
          values,
          identifier: updatedData.identifier,
          recordedAt: new Date().toISOString(),
          customProperties: {},
        };

        break;
      case "Gateways":
        path = "gateways";
        console.log("[Gateway Update] updatedData:", updatedData);
        body = {
          id: updatedData.id,
          siteId: selectedSite.id,
          tenantId: selectedSite.tenantId,
          name: updatedData.name,
          macAddress: updatedData.macAddress,
          queue: updatedData.queue,
          manufacturer: updatedData.manufacturer,
          model: updatedData.model,
        };
        console.log("[Gateway Update] body being sent:", body);
        break;
      case "Digital Tags":
        path = "digitalTag";
        body = {
          id: updatedData.id,
          tenantId: selectedSite.tenantId,
          siteId: selectedSite.id,
          name: updatedData.name,
          macAddress: updatedData.macAddress,
          customProperties: { rotation: updatedData.rotation },
        };
        break;

      case "StoreSettings":
        path = "data-info";
        body = {
          id: updatedData.id,
          tenantId: selectedSite.tenantId,
          siteId: selectedSite.id,
          name: updatedData.name,
          fields: updatedData.fields,
        };
        break;

      default:
        console.error("Unknown selected option. Cannot save data.");
        return;
    }

    try {
      const apiResponse = await callPiggybackAPI(`${path}`, {}, body, "POST");

      if (apiResponse) {
        toast.success("Record updated!");
        setData((prev) =>
          prev.map((row) =>
            row.id === updatedData.id ? { ...row, ...updatedData } : row
          )
        );
        if (path === "data-record" && data?.length > 0) {
          dispatch(setTruInkDataInfoId(data[0].dataInfoId));
        }
        setIsEditModalOpen(false);
      }
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Update failed. Please try again.");
      setIsEditModalOpen(false);
    }
  };

  const handleDeleteClick = (path, id) => {
    setDeleteConfirm({ type: "row", path, id });
  };

  const executeDelete = async () => {
    if (!deleteConfirm) return;

    if (deleteConfirm.type === "row") {
      if (!selectedSite) { setDeleteConfirm(null); return; }
      try {
        await callPiggybackAPI(
          deleteConfirm.path,
          { tenantId: selectedSite.tenantId, siteId: selectedSite.id, id: deleteConfirm.id },
          {},
          "DELETE"
        );
        toast.success("Record deleted!");
        setData((prev) => prev.filter((row) => row.id !== deleteConfirm.id));
      } catch (err) {
        console.error("Delete failed:", err);
        toast.error("Delete failed. Please try again.");
      }
    }

    if (deleteConfirm.type === "field") {
      const updatedFields = data.fields.filter((f) => f.name !== deleteConfirm.field.name);
      const fieldsObject = updatedFields.reduce((acc, f) => {
        acc[f.name] = Array.isArray(f.value) ? f.value : f.value || "";
        return acc;
      }, {});
      const body = {
        ...data,
        id: data.id,
        tenantId: selectedSite.tenantId,
        siteId: selectedSite.id,
        fields: fieldsObject,
      };
      try {
        await callPiggybackAPI("data-info", {}, body, "POST");
        toast.success("Field deleted!");
        setData({ ...data, fields: updatedFields });
      } catch (err) {
        console.error("Failed to delete field:", err);
        toast.error("Delete failed. Please try again.");
      }
    }

    setDeleteConfirm(null);
  };

  const handleLinkClick = async (tagData) => {
    setTagToLink(tagData);
    setIsLinkModalOpen(true);
    setLoadingOptions(true);

    try {
      const templatePath = "templates";
      const dataRecordPath = "data-record";
      const commonParams = {
        tenantId: selectedSite.tenantId,
        siteId: selectedSite.id,
      };

      const templatesResponse = await callPiggybackAPI(
        templatePath,
        commonParams,
        {},
        "GET"
      );
      const dataRecordsResponse = await callPiggybackAPI(
        dataRecordPath,
        commonParams,
        {},
        "GET"
      );

      if (Array.isArray(templatesResponse)) {
        setTemplateOptions(templatesResponse);
      }
      if (Array.isArray(dataRecordsResponse)) {
        setDataRecordOptions(
          dataRecordsResponse.map((item) => ({
            ...item,
            name: item.values?.name || item.id,
          }))
        );
      }
    } catch (err) {
      console.error("Failed to fetch dropdown options:", err);
      toast.error("Failed to fetch templates or data records.");
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleConfirmLink = async (templateId, dynamicDataId) => {
    if (!tagToLink) return;
    setIsLinkModalOpen(false);

    const { tenantId, siteId, id: digitalTagId } = tagToLink;
    if (
      templateId.truInkOccupancyFreeTemplate &&
      templateId.truInkOccupancyBusyTemplate
    ) {
      try {
        const apiResponse = await callPiggybackAPI(
          "digitalTag/bindTemplateData",
          {},
          {
            tenantId,
            siteId,
            templateId,
            dynamicDataId,
            truInkOccupancyFreeTemplate: templateId.truInkOccupancyFreeTemplate,
            truInkOccupancyBusyTemplate: templateId.truInkOccupancyBusyTemplate,
            digitalTagIds: [digitalTagId],
          },
          "POST"
        );

        if (apiResponse) {
          toast.success("Tags successfully linked!");
        } else {
          toast.error("Linking failed. Please try again.");
        }
      } catch (error) {
        console.error("Unexpected error during linking:", error);
        toast.error("An unexpected error occurred during linking.");
      }
    } else {
      try {
        const apiResponse = await callPiggybackAPI(
          "digitalTag/bindTemplateData",
          {},
          {
            tenantId,
            siteId,
            templateId,
            dynamicDataId,
            digitalTagIds: [digitalTagId],
          },
          "POST"
        );

        if (apiResponse) {
          toast.success("Tags successfully linked!");
        } else {
          toast.error("Linking failed. Please try again.");
        }
      } catch (error) {
        console.error("Unexpected error during linking:", error);
        toast.error("An unexpected error occurred during linking.");
      }
    }
    setTagToLink(null);
  };
  const handleApplyCollectionProfile = async (collectionId) => {
    setCollectionApplyModalOpen(true);
    setSelectedCollectionForEdit(collectionId);
    const getTemplates = await callPiggybackAPI(
      "templates",
      { tenantId: selectedSite.tenantId, siteId: selectedSite.id },
      {},
      "GET"
    );
    setTemplates(getTemplates);
  };
  const handleApplyCollectoion = async (templateId) => {
    const body = {
      collectionId: selectedCollectionForEdit,
      templateId,
      siteId: selectedSite.id,
      tenantId: selectedSite.tenantId,
    };
    try {
      const apiResponse = await callPiggybackAPI(
        "collections/brush",
        {},
        body,
        "POST"
      );

      if (apiResponse) {
        toast.success("Collection Applied Successfully", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } else {
        toast.error(apiResponse, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    } catch (error) {
      toast.error(error.message, { position: toast.POSITION.BOTTOM_RIGHT });
    }
  };
  useEffect(() => {
    if (selectedSiteOption === "SiteTemplates") {
      setData([]);
      setLoading(false);
      return;
    }

    if (!selectedSite || !selectedSiteOption) {
      setData([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      let path = "";

      switch (selectedSiteOption) {
        case "SiteData":
          try {
            const [schemaResponse, recordsResponse] = await Promise.all([
              callPiggybackAPI("data-info", { siteId: selectedSite.id, tenantId: selectedSite.tenantId }, {}, "GET"),
              callPiggybackAPI("data-record", { siteId: selectedSite.id, tenantId: selectedSite.tenantId }, {}, "GET"),
            ]);

            const schemaFields = schemaResponse?.[0]?.fields
              ? Object.keys(schemaResponse[0].fields)
              : [];

            if (Array.isArray(recordsResponse)) {
              const normalizedData = recordsResponse.map((item, index) => {
                const trimmedValues = Object.entries(item.values || {}).reduce((acc, [k, v]) => {
                  acc[k.trim()] = v;
                  return acc;
                }, {});
                const normalizedValues = schemaFields.reduce((acc, key) => {
                  acc[key] = trimmedValues[key] ?? "";
                  return acc;
                }, {});

                if (index === 0) {
                  dispatch(setSiteSchema({ schema: dataRecordSchema(normalizedValues) }));
                }

                return {
                  id: item.id || `row-${index}`,
                  identifier: item.identifier,
                  dataInfoId: item.dataInfoId,
                  ...normalizedValues,
                };
              });

              const processedData = processTableData(normalizedData, "data-record", {
                handleEditClick,
                handleDeleteClick,
                handleLinkClick,
                refreshDigitalTags,
                handleApplyCollectionProfile,
              });

              setData(processedData);
            } else {
              setData([]);
            }
          } catch (err) {
            console.error("Failed to fetch SiteData:", err);
            setError("Failed to fetch data. Please try again later.");
            setData([]);
          } finally {
            setLoading(false);
          }
          return;
        case "Gateways":
          path = "gateways";
          break;
        case "Digital Tags":
          path = "digitalTag";
          break;
        case "Collections":
          path = "collections";
          break;
        case "TruInk EMMS":
          path = "truInkEmms";
          break;
        case "StoreSettings":
          try {
            const apiResponse = await callPiggybackAPI(
              "data-info",
              { siteId: selectedSite.id, tenantId: selectedSite.tenantId },
              {},
              "GET"
            );

            if (apiResponse) {
              const schema = {
                id: apiResponse[0].id,
                ...buildDataInfoSchema(
                  apiResponse[0].name,
                  apiResponse[0].fields
                ),
              };
              setData(schema);
            } else {
              setData(null);
            }
          } catch (err) {
            console.error("Failed to fetch store settings:", err);
            setError("Failed to fetch store settings.");
            setData(null);
          } finally {
            setLoading(false);
          }
          return;

        case "Reports":
          setData([]);
          setLoading(false);
          return;
        default:
          console.error("Unknown option:", selectedSiteOption);
          setData([]);
          setLoading(false);
          return;
      }

      try {
        const apiResponse = await callPiggybackAPI(
          path,
          { siteId: selectedSite.id, tenantId: selectedSite.tenantId },
          {},
          "GET"
        );

        if (Array.isArray(apiResponse)) {
          const normalizedData = apiResponse.map((item, index) => {
            if (path == "digitalTag") {
              const { binding, ...rest } = item;
              return {
                id: item.id || `row-${index}`,
                ...rest,
                templateName: binding?.template?.name ?? null,
                dynamicData: binding?.dynamicData?.identifier ?? null,
              };
            } else if (path === "truInkEmms") {
              return {
                id: item.id || `row-${index}`,
                name: item.name,
                emmsName: item.emmsName,
                emmsId: item.emmsId,
                siteId: item.siteId,
                tenantId: item.tenantId,
              };
            } else if (path === "collections") {
              return {
                id: item.id || `row-${index}`,
                name: item.name,
                dataIds: item.dataIds,
              };
            }
            return {
              id: item.id || `row-${index}`,
              ...item,
            };
          });

          const processedData = processTableData(normalizedData, path, {
            handleEditClick,
            handleDeleteClick,
            handleLinkClick,
            refreshDigitalTags,
            handleApplyCollectionProfile,
          });

          setData(processedData);
        } else {
          setData([]);
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to fetch data. Please try again later.");
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedSiteOption, selectedSite, refreshKey]);

  const columns =
    data?.length > 0
      ? Object.keys(data[0]).map((key) => {
        let colDef = {
          field: key,
          headerName: key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase()),
          flex: 1,
          minWidth: key === "id" ? 300 : 150,
          renderCell: (params) =>
            typeof params.value === "object" ? params.value : params.value,
        };
        if (key === "actions") {
          colDef = {
            ...colDef,
            flex: 2,
            minWidth: 200,
            sortable: false,
            filterable: false,
          };
        }
        if (key === "dataIds") {
          colDef = { ...colDef, maxWidth: 300 };
        }
        return colDef;
      })
      : [];


  const hiddenMacAddressData = useMemo(() => {
    if (selectedSiteOption !== "Digital Tags" && selectedSiteOption !== "Gateways") {
      return data;
    }

    return (data || []).map((d) => {
      const mac = d.macAddress || "";

      return {
        ...d,
        macAddress: mac
          ? "*".repeat(Math.max(0, mac.length - 4)) + mac.slice(-4)
          : "",
      };
    });
  }, [data]);

  if (loading) {
    return (
      <Box className={classes.loaderContainer}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "red" }}>
        <Typography variant="h6">{error}</Typography>
      </Box>
    );
  }

  if (selectedSiteOption === "SiteTemplates") {
    return <AllTemplates
      templateSearchValue={templateSearchValue}
      setTemplateSearchValues={setTemplateSearchValues}
    />;
  }
  if (selectedSiteOption === "StoreSettings") {
    if (!data?.fields) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6">No settings schema found</Typography>
        </Box>
      );
    }

    const handleEdit = (field) => {
      setFieldToEdit({ ...field, originalName: field.name });
      setIsFieldEditOpen(true);
    };
    const handleAdd = () => {
      setFieldToEdit({ name: "", label: "", value: "" });
      setIsFieldEditOpen(true);
    };

    const handleDelete = (field) => {
      setDeleteConfirm({ type: "field", field });
    };

    return (
      <div className={classes.container}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleAdd()}
            sx={{ border: "1px solid #ddd" }}
          >
            <Add fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ maxHeight: "50vh", overflowY: "scroll", pr: 1 }}>
          <TableContainer component={Paper} sx={{ maxHeight: "40vh" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody sx={{ background: "rgb(249,249,249)", position: "relative" }}>
                {data.fields.map((field, index) => (
                  <TableRow key={field.name}>
                    {/* Use index as ID */}
                    <TableCell>{index + 1}</TableCell>

                    <TableCell>{field.type || "-"}</TableCell>

                    {/* Show label for a nicer name */}
                    <TableCell>{field.label || field.name || "-"}</TableCell>

                    {/* Handle arrays vs simple values */}
                    <TableCell>
                      {Array.isArray(field.value)
                        ? field.value.join(", ")
                        : field.value || "-"}
                    </TableCell>

                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(field)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(field)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Dialog
          open={isFieldEditOpen}
          onClose={() => setIsFieldEditOpen(false)}
        >
          <DialogTitle>
            {fieldToEdit?.originalName ? "Edit Field" : "Add Field"}
          </DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              label="Name"
              value={fieldToEdit?.name || ""}
              onChange={(e) =>
                setFieldToEdit({
                  ...fieldToEdit,
                  name: e.target.value,
                  label: e.target.value,
                })
              }
              fullWidth
            />
            <TextField
              label="Value"
              value={
                Array.isArray(fieldToEdit?.value)
                  ? fieldToEdit?.value.join(", ")
                  : fieldToEdit?.value || ""
              }
              onChange={(e) =>
                setFieldToEdit({
                  ...fieldToEdit,
                  value: e.target.value.includes(",")
                    ? e.target.value.split(",").map((v) => v.trim())
                    : e.target.value,
                })
              }
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsFieldEditOpen(false)}
              sx={{
                textTransform: "none",
                background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                color: "#fff",
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                  opacity: 0.95,
                  boxShadow: "none"
                },
                "&.Mui-disabled": {
                  background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                  opacity: 0.5,
                  color: "#fff"
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                let updatedFields;

                if (fieldToEdit?.originalName) {
                  updatedFields = data.fields.map((f) =>
                    f.name === fieldToEdit.originalName ? fieldToEdit : f
                  );
                } else {
                  updatedFields = [...data.fields, fieldToEdit];
                }

                const fieldsObject = updatedFields.reduce((acc, f) => {
                  acc[f.name] = Array.isArray(f.value)
                    ? f.value
                    : f.value || "";
                  return acc;
                }, {});

                const body = {
                  ...data,
                  id: data.id,
                  tenantId: selectedSite.tenantId,
                  siteId: selectedSite.id,
                  fields: fieldsObject,
                };

                try {
                  await callPiggybackAPI("data-info", {}, body, "POST");
                  toast.success(
                    fieldToEdit?.originalName
                      ? "Field updated!"
                      : "Field added!"
                  );
                  setData({ ...data, fields: updatedFields });
                  setIsFieldEditOpen(false);
                } catch (err) {
                  console.error("Save failed:", err);
                  toast.error("Save failed. Please try again.");
                }
              }}

              sx={{
                color:"white",
                textTransform: "none",
                backgroundColor: "rgb(90, 28, 47)",
                "&:hover": {
                  backgroundColor: "rgb(70, 20, 35)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(90, 28, 47, 0.4)",
                  color: "#fff",
                },
              }}

            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this {deleteConfirm?.type === "field" ? "field" : "record"}? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteConfirm(null)}
              sx={{ textTransform: "none", background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))", color: "#fff", boxShadow: "none", "&:hover": { opacity: 0.95 } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={executeDelete}
              sx={{ textTransform: "none", backgroundColor: "rgb(90, 28, 47)", "&:hover": { backgroundColor: "rgb(70, 20, 35)" } }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  if (data?.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">
          No data to display for {selectedSiteOption}.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <div className={classes.container}>
        <DataGrid
          rows={hiddenMacAddressData}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          columns={columns}
          disableRowSelectionOnClick
          autoHeight
          checkboxSelection
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[3]}
          onRowSelectionModelChange={(newSelection) => {
            setSelectedIds(newSelection);
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 4 },
            },
          }}
          sx={{ background: "rgb(249,249,249)" }}
        />
      </div>

      <LinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onConfirm={handleConfirmLink}
        templateOptions={templateOptions.filter(t => {
          const tagSizeId = tagToLink?.canvasSizeId ?? tagToLink?.canvasSize?.id;
          const tagColorId = tagToLink?.canvasColorId ?? tagToLink?.canvasColor?.id;
          const hasSize = tagSizeId != null && tagSizeId !== "";
          const hasColor = tagColorId != null && tagColorId !== "";
          if (!hasSize && !hasColor) return true;
          const sizeMatch = hasSize ? String(t.canvasSizeId) === String(tagSizeId) : true;
          const colorMatch = hasColor ? String(t.canvasColorId) === String(tagColorId) : true;
          return sizeMatch && colorMatch;
        })}
        dataRecordOptions={dataRecordOptions}
        loadingOptions={loadingOptions}
        handleRefreshTag={async () => {
          const apiResponse = await callPiggybackAPI(
            "digitalTag/refresh",
            {
              siteId: selectedSite.id,
              tenantId: selectedSite.tenantId,
            },
            {
              digitalTagIds: [tagToLink.id],
            },
            "POST"
          );
        }}
      />

      <TruInkEditForm
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        data={editRowData}
        schema={currentSchema}
        title={`Edit ${selectedSiteOption}`}
        onSubmit={handleUpdateSubmit}
      />
      <AddTruInkEMMSModal
        open={isEmmsModalOpen}
        onClose={() => {
          setIsEmmsModalOpen(false);
          setEmmsSavedData(null);
        }}
        onConfirm={() => {
          setIsEmmsModalOpen(false);
          setEmmsSavedData(null);
        }}
        savedData={emmsSavedData}
      />
      <TrueInkCollectionProfileModal
        open={collectionApplyModalOpen}
        onClose={() => setCollectionApplyModalOpen(false)}
        templates={templates}
        onSave={handleApplyCollectoion}
      />
      <AddCollectionModal
        open={collectionEditClicked}
        onClose={() => {
          setCollectionEditClicked(false);
          setSelectedCollectionData(null);
        }}
        onConfirm={(updatedData) => {
          if (updatedData.items.length === 0) {
            return enqueueSnackbar("Please select at least one item.", {
              variant: "warning",
              autoHideDuration: 3000,
              preventDuplicate: true,
            });
          } else {
            handleCollectionEditSave(updatedData)
              .then(() => {
                setCollectionEditClicked(false);
                setSelectedCollectionData(null);
                onSaveSuccess();
              })
              .catch((err) => {
                enqueueSnackbar("Error saving collection.", {
                  variant: "error",
                  autoHideDuration: 3000,
                  preventDuplicate: true,
                });
              });
          }
        }}
        dataRecords={dataRecords}
        savedData={selectedCollectionData}
      />

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this record? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirm(null)}
            sx={{ textTransform: "none", background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))", color: "#fff", boxShadow: "none", "&:hover": { opacity: 0.95 } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={executeDelete}
            sx={{ textTransform: "none", backgroundColor: "rgb(90, 28, 47)", "&:hover": { backgroundColor: "rgb(70, 20, 35)" } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TrunInkDataTable;
